import { CallDto } from '@act/shared/data-transfer-objects';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from '../storeHooks';
import { CallState } from '../types/callsTypes';
import { ActivitySelectorParams } from '../types/selectorTypes';
import { callsAdapter, voiceCallReviewAdapter } from './callsSliceState.util';

export const selectCallSliceState = (state: { calls?: CallState }) =>
  state?.calls;

const selectCallsState = createSelector(selectCallSliceState, s => s.calls);

export const { selectAll: selectCalls, selectEntities: selectCallsMap } =
  callsAdapter.getSelectors(selectCallsState);

export const selectCallById = (id: number) =>
  createSelector(selectCallsMap, callsMap => callsMap[id]);

export const selectCallByInteractionId = (interactionId: number | undefined) =>
  createSelector(selectCalls, calls => {
    if (interactionId == null) {
      return undefined;
    }

    return calls.find(call => call.interactionId === interactionId);
  });

export const selectCallFilters = createSelector(
  selectCallSliceState,
  s => s.callFilterOptions
);

export const selectCallFilterLoadingStatus = createSelector(
  selectCallSliceState,
  s => s.callFilterOptionStatus
);

const {
  selectById: selectVoiceCallAuditById,
  selectAll: selectVoiceCallReviews,
} = voiceCallReviewAdapter.getSelectors();

const selectCallsAreLoading = (state: { calls?: CallState }) =>
  selectCallSliceState(state).calls.status;
const selectCallsReviewsAreLoading = (state: {
  calls?: CallState;
}) => selectCallSliceState(state).voiceCallReviews.status;

const selectCallsPage = createSelector(selectCallSliceState, state =>
  state.callPageIds.map(id => state.calls.entities[id])
);

export const selectCallsPageTotal = (state: {
  calls?: CallState;
}) => state?.calls.callPageTotalCount;

export const selectCallsByGuideAndTime: (
  state: unknown,
  paramObj: ActivitySelectorParams
) => CallDto[] = createSelector(
  [
    selectCalls,
    (_state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (_state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (_state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (calls, guideId, startEpochTime, endEpochTime) =>
    calls.filter(
      c =>
        (c.originator?.userId === guideId || c.receiver?.userId === guideId) &&
        c.timestamp >= startEpochTime &&
        c.timestamp <= endEpochTime
    )
);

const selectvoiceCallReviewsState = createSelector(
  selectCallSliceState,
  s => s.voiceCallReviews
);

export const selectVoiceCallReviewsByCallId = createSelector(
  [selectvoiceCallReviewsState, (_state, callId: number) => callId],
  (auditsState, callId) => {
    const audits = Object.values(auditsState.entities || {});
    return audits.filter(
      audit => audit && Number(audit.callId) === callId
    );
  }
);

export const selectVoiceCallReviewsByAssigneeId = createSelector(
  [selectvoiceCallReviewsState, (_state, assigneeId: string) => assigneeId],
  (auditsState, assigneeId) => {
    const audits = Object.values(auditsState.entities || {});
    return audits.filter(audit => audit && audit.assigneeId === assigneeId);
  }
);

export const selectSuggestionsByCallId = createSelector(
  [selectCallSliceState, (_state, callId: number) => callId],
  (callState, callId) => callState.suggestions[callId] || null
);

export const selectSuggestionsLoadingStatusByCallId = createSelector(
  [selectCallSliceState, (_state, callId: number) => callId],
  (callState, callId) => callState.suggestionsLoadingStatus[callId] || 'initial'
);

/**
 * Loading status for GET /calls/:id detail hydration (transcript / LLM notes).
 * @param state Root state with calls slice.
 * @param callId Voice call id, or null when no call is selected.
 * @returns Per-call detail status, or `initial` when never requested / no id.
 */
export const selectCallDetailStatusById = createSelector(
  [
    selectCallSliceState,
    (_state, callId: number | null | undefined) => callId,
  ],
  (callState, callId) =>
    callId == null
      ? 'initial'
      : callState.callDetailStatusById?.[callId] || 'initial'
);

export const selectJustInTimeCalls = createSelector(
  selectCallSliceState,
  s =>
    Object.values(s.justInTimeCalls).filter(
      call => call.id !== s.minimizedCallId
    )
);

export const selectMinimizedCallId = createSelector(
  selectCallSliceState,
  s => s.minimizedCallId
);

export const callsAppSelectors = {
  useSelectCallById: (id: number) => useAppSelector(selectCallById(id)),
  useSelectCallByInteractionId: (interactionId: number | undefined) =>
    useAppSelector(selectCallByInteractionId(interactionId)),
  useSelectAllCalls: () => useAppSelector(selectCalls),
  useSelectCallsPage: () => useAppSelector(selectCallsPage),
  useSelectCallsPageTotal: () => useAppSelector(selectCallsPageTotal),
  useSelectCallsFilters: () => useAppSelector(selectCallFilters),
  useSelectCallsFiltersLoadingStatus: () =>
    useAppSelector(selectCallFilterLoadingStatus),
  useSelectCallsAreLoading: () => useAppSelector(selectCallsAreLoading),
  useSelectCallsReviewsAreLoading: () =>
    useAppSelector(selectCallsReviewsAreLoading),
  useSelectVoiceCallReviewsByCallId: (callId: number) =>
    useAppSelector(state => selectVoiceCallReviewsByCallId(state, callId)),
  useSelectVoiceCallReviewsByAssigneeId: (assigneeId: string) =>
    useAppSelector(state =>
      selectVoiceCallReviewsByAssigneeId(state, assigneeId)
    ),
  useSelectVoiceCallAuditById: (id: number) =>
    useAppSelector(state =>
      selectVoiceCallAuditById(state.calls.voiceCallReviews, id)
    ),
  useSelectAllCallReviews: () =>
    useAppSelector(state =>
      selectVoiceCallReviews(selectvoiceCallReviewsState(state))
    ),
  useSelectSuggestionsByCallId: (callId: number) =>
    useAppSelector(state => selectSuggestionsByCallId(state, callId)),
  useSelectSuggestionsLoadingStatusByCallId: (callId: number) =>
    useAppSelector(state =>
      selectSuggestionsLoadingStatusByCallId(state, callId)
    ),
  useSelectCallDetailStatusById: (callId: number | null | undefined) =>
    useAppSelector(state => selectCallDetailStatusById(state, callId)),
};

export { selectVoiceCallAuditById, selectVoiceCallReviews };
